import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useApolloClient, useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";
import { useNavigate, Path } from "@/router";
import AppLayout from "@/layouts/AppLayout";
import PageHeading from "@/components/PageHeading";
import Autocomplete from "@/components/Autocomplete";
import ListItem from "@/components/ListItem";
import Button from "@/components/Button";
import Banner from "@/components/Banner";
import Avatar from "@/components/Avatar";
import SubSectionHeading from "@/components/SubSectionHeading";
import { TableHeading } from "@/components/Table";
import usePageTitle from "@/hooks/usePageTitle";
import useCurrentUser from "@/hooks/useCurrentUser";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { SOME_USERS_QUERY, type BackofficeUserOption } from "@/pages/people/queries";
import { useAssumeUser } from "@/providers/AssumeUserProvider";
import { getInternalReturnUrl, readAssumedUser } from "@/providers/AssumeUserProvider/storage";
import { CAPABILITIES_QUERY } from "@/providers/AuthorizationProvider/queries";
import { getImpersonationFailureFromError, type ImpersonationFailure } from "@/providers/GraphqlProvider/errors";
import { getImpersonationHeaders } from "@/providers/GraphqlProvider/headers";

const MIN_SEARCH_LENGTH = 2;
const PREFLIGHT_FALLBACK_ERROR: ImpersonationFailure = {
  code: "IMPERSONATION_FAILED",
  reason: "UNKNOWN",
  message: "We couldn't verify this user's Member 360 access. Try again later.",
  retryable: true
};
const PREFLIGHT_TARGET_MISMATCH_ERROR: ImpersonationFailure = {
  code: "IMPERSONATION_FAILED",
  reason: "TARGET_NOT_CONFIRMED",
  message: "We couldn't verify that Member 360 switched to the selected user. Try again.",
  retryable: true
};
const RESET_STORE_ERROR: ImpersonationFailure = {
  code: "IMPERSONATION_FAILED",
  reason: "IMPERSONATION_SETUP_FAILED",
  message: "We couldn't clear cached Member 360 data before switching users. Try again.",
  retryable: true
};

const REASON_LABELS: Record<string, string> = {
  ACTOR_NOT_PERMITTED: "You are not permitted to assume users",
  FEATURE_DISABLED: "Assumed-user access is disabled",
  FEATURE_FLAG_UNAVAILABLE: "Assumed-user access is temporarily unavailable",
  INVALID_HEADER: "The assumed-user request was invalid",
  TARGET_NOT_FOUND: "The selected user could not be found",
  TARGET_NOT_CONFIRMED: "The selected user could not be confirmed",
  IMPERSONATION_SETUP_FAILED: "Assumed-user setup failed",
  UNSUPPORTED_PROVIDER: "The selected user is not linked to a supported identity provider",
  INVALID_PROVIDER_IDENTIFIER: "The selected user's identity provider record is incomplete",
  MISSING_AUTHORIZATION_PROFILE: "The selected user's authorization profile is missing",
  MALFORMED_AUTHORIZATION_PROFILE: "The selected user's authorization profile is incomplete",
  NO_M360_ROLES: "The selected user has no active Member 360 backoffice role",
  NO_M360_CAPABILITIES: "The selected user has no active Member 360 capabilities",
  IDENTITY_PROVIDER_UNAVAILABLE: "The identity provider could not be reached"
};

function getReasonLabel(reason: string): string {
  return REASON_LABELS[reason] ?? reason.replace(/_/g, " ").toLowerCase();
}

function AssumeUserNotes() {
  return (
    <section
      className="mt-8 rounded-2xl border border-sys-brand-outline bg-sys-brand-surface p-6 shadow-web-1 sm:p-8"
      data-testid="assume-user-notes"
    >
      <TableHeading heading="Additional Information" topSpacing="none" />
      <ul className="list-disc space-y-2 pl-5 text-body-medium">
        <li>
          <strong>You are able to execute actions on behalf of the assumed user</strong>, but we will track that
          it&apos;s you in the backend.
        </li>
        <li>
          Recently Visited shows the assumed user&apos;s history, not yours. Leftover recents in this browser are not
          imported while you are assuming someone.
        </li>
        <li>
          Inactive users who are still in our system cannot be assumed. You&apos;ll get an error message when you try.
        </li>
        <li>Amplitude analytics will track the authenticated user, not the assumed user.</li>
        <li>
          This feature is <strong>NOT</strong> available for the M360 portal site, yet.
        </li>
      </ul>
    </section>
  );
}

export default function AssumeUserPage() {
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.assumeUser });
  usePageTitle("/assume-user", "Assume a User");

  const navigate = useNavigate();
  const client = useApolloClient();
  const { user } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const { assumedUser, startAssumingUser, confirmAssumedUserAuthorization, stopAssumingUser, clearAssumedUserSession } =
    useAssumeUser();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<BackofficeUserOption | null>(null);
  const [preflightError, setPreflightError] = useState<ImpersonationFailure | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const returnUrl = getInternalReturnUrl(searchParams.get("return_to"));

  const {
    data,
    loading,
    error: searchError,
    refetch
  } = useQuery(SOME_USERS_QUERY, {
    variables: { search: debouncedSearchTerm, forImpersonation: true },
    skip: debouncedSearchTerm.length < MIN_SEARCH_LENGTH || !!assumedUser
  });

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const clearSelection = () => {
    setSelectedUser(null);
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPreflightError(null);
  };

  const handleAssumeUser = async () => {
    const targetUser = selectedUser;
    if (!targetUser || isStarting) return;

    setIsStarting(true);
    setPreflightError(null);

    try {
      const { data: preflightData } = await client.query({
        query: CAPABILITIES_QUERY,
        context: { headers: getImpersonationHeaders(targetUser.id) },
        fetchPolicy: "no-cache"
      });

      if (preflightData?.me?.id !== targetUser.id) {
        setPreflightError(PREFLIGHT_TARGET_MISMATCH_ERROR);
        return;
      }

      flushSync(() => {
        startAssumingUser({
          id: targetUser.id,
          name: targetUser.name,
          email: targetUser.email,
          photo: targetUser.photo,
          department: targetUser.department,
          returnUrl
        });
      });

      try {
        await client.resetStore();
      } catch {
        clearAssumedUserSession(targetUser.id);
        setPreflightError(RESET_STORE_ERROR);
        return;
      }

      const activeAssumedUser = readAssumedUser(user.sub);
      if (activeAssumedUser?.id !== targetUser.id) {
        return;
      }

      confirmAssumedUserAuthorization();
      navigate(returnUrl as Path);
    } catch (error) {
      clearAssumedUserSession(targetUser.id);
      setPreflightError(getImpersonationFailureFromError(error) ?? PREFLIGHT_FALLBACK_ERROR);
    } finally {
      setIsStarting(false);
    }
  };

  const handleStopAssumingUser = () => {
    const activeReturnUrl = getInternalReturnUrl(assumedUser?.returnUrl);
    stopAssumingUser();
    navigate(activeReturnUrl as Path);
  };

  return (
    <AppLayout interior>
      <div className="mx-auto max-w-3xl pb-12">
        <PageHeading
          title="Assume a User"
          containerClassName="pt-6 text-center lg:pt-8"
          titleClassName="text-sys-on-surface text-headline-medium-bold font-serif lg:text-headline-large-bold"
        />
        {assumedUser && !isStarting ? (
          <div className="pt-8">
            <Banner
              variant="info"
              description={`You are already viewing Member 360 as ${assumedUser.name}.`}
              subText="Stop assuming this user before starting another session."
              primaryAction={{ label: "Stop assuming", onClick: handleStopAssumingUser }}
              hideSecondaryDismiss
              testId="assume-user-already-active"
            />
          </div>
        ) : (
          <form
            className="mt-8 overflow-hidden rounded-2xl border border-sys-brand-outline bg-sys-brand-surface shadow-web-1"
            autoComplete="off"
            onSubmit={(event) => {
              event.preventDefault();
              handleAssumeUser();
            }}
          >
            <div className="flex items-center gap-4 border-b border-sys-brand-outline-variant bg-sys-brand-surface-container-lowest px-6 py-5 sm:px-8">
              <Avatar className="shrink-0" iconClassName="text-sys-brand-on-secondary-container" />
              <p className="text-body-medium text-sys-brand-on-surface-variant">
                Search for a user to view Member 360 with their access and permissions.
              </p>
            </div>
            <div className="space-y-6 p-6 sm:p-8">
              <Autocomplete<BackofficeUserOption>
                autofocus
                label="User"
                name="search"
                type="search"
                data={data?.backofficeUsers ?? []}
                loadingResults={loading}
                query={selectedUser?.name ?? searchTerm}
                onChangeText={(value) => {
                  if (selectedUser) setSelectedUser(null);
                  setPreflightError(null);
                  setSearchTerm(value);
                }}
                onSelect={(user) => {
                  setSelectedUser(user);
                  setPreflightError(null);
                  setSearchTerm(user.name);
                }}
                reset={clearSelection}
                renderItem={(user) => (
                  <ListItem
                    compact
                    useAvatar
                    picture={user.photo ?? undefined}
                    headline={user.name}
                    supportingText={[user.email, user.department].filter(Boolean).join(" · ")}
                  />
                )}
                variant="searchableDropdown"
                leadingIcon="search"
                menuPositioning="fixed"
                menuClassName="max-h-[300px]"
                constrainMenuWidth
                data-testid="assume-user-search"
              />

              {searchError ? (
                <Banner
                  variant="error"
                  description="We couldn’t search for users in Member 360."
                  subText="Try again or contact Technology if the problem continues."
                  primaryAction={{ label: "Try again", onClick: () => void refetch() }}
                  testId="assume-user-search-error"
                />
              ) : null}

              {preflightError ? (
                <Banner
                  variant="error"
                  description={preflightError.message}
                  subText={`Reason: ${getReasonLabel(preflightError.reason)}.`}
                  testId="assume-user-preflight-error"
                />
              ) : null}

              {selectedUser ? (
                <div className="rounded-lg border border-sys-brand-outline-variant bg-sys-brand-surface-container-low p-4">
                  <SubSectionHeading className="mb-2">Selected User</SubSectionHeading>
                  <ListItem
                    compact
                    useAvatar
                    picture={selectedUser.photo ?? undefined}
                    headline={selectedUser.name}
                    supportingText={[selectedUser.email, selectedUser.department].filter(Boolean).join(" · ")}
                  />
                </div>
              ) : null}

              <div className="flex flex-wrap justify-end gap-3 pt-6">
                <Button variant="text" type="button" onClick={() => navigate(returnUrl as Path)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!selectedUser || isStarting}>
                  {isStarting ? "Verifying user..." : "Assume User"}
                </Button>
              </div>
            </div>
          </form>
        )}
        <AssumeUserNotes />
      </div>
    </AppLayout>
  );
}
